import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'

import { ToastProps } from '.'

import styles from './styles.module.scss'

import { clearTimeoutIfExists } from '@/utils'

const TOAST_TIMEOUT_MS = 2000
const TOAST_EXIT_MS = 200

interface ToastContextType {
  toastProps: ToastProps
  updateToast: (message: string, type: 'success' | 'error' | 'progress', timeoutOverride?: number) => void
  clearToast: (timeoutOverride?: number) => void
}

const RESTING_PROPS: ToastProps = {
  message: '',
  type: 'success',
  animationClass: styles.hidden,
}

const ToastContext = createContext<ToastContextType | null>(null)

export const ToastContextProvider = ({ children }: PropsWithChildren) => {
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const toastExitTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [toastProps, setToastProps] = useState<ToastProps>(RESTING_PROPS)

  const clearToast = async (timeoutOverride?: number): Promise<void> => {
    return new Promise((resolve) => {
      clearTimeoutIfExists(toastTimeoutRef.current)
      clearTimeoutIfExists(toastExitTimeoutRef.current)
      setToastProps((prev) => ({
        ...prev,
        animationClass: prev.animationClass === styles.hidden ? styles.hidden : styles.exit,
      }))

      const exitTime = timeoutOverride ?? TOAST_EXIT_MS
      toastExitTimeoutRef.current = setTimeout(() => {
        setToastProps((prev) => ({
          ...prev,
          animationClass: styles.hidden,
        }))
        // Resolve after state has been updated
        setTimeout(() => resolve(), 10)
      }, exitTime)
    })
  }

  const updateToast = async (message: string, type: 'success' | 'error' | 'progress', timeoutOverride?: number) => {
    if (toastProps.animationClass !== styles.hidden) {
      await clearToast()
    }
    setToastProps({ message, type, animationClass: styles.enter })
    toastTimeoutRef.current = setTimeout(() => {
      setToastProps({ message, type, animationClass: styles.exit })

      toastExitTimeoutRef.current = setTimeout(() => {
        setToastProps(RESTING_PROPS)
      }, TOAST_EXIT_MS)
    }, timeoutOverride ?? TOAST_TIMEOUT_MS)
  }

  return <ToastContext.Provider value={{ toastProps, updateToast, clearToast }}>{children}</ToastContext.Provider>
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastContextProvider')
  }
  return context
}
